import { DecimalPipe } from '@angular/common';
import moment from 'moment';
import { GameDayModel } from '../../models/gameday-model';
import { BarChartLegend } from '../../shared/models/charts/chart-data-types';
import { NoRoundPercentsPipe } from '../../profile/pipes/no-round-percents.pipe';
import {
  Tranche,
  TrancheGroup
} from '../../gameday/data/gameDay-data.transformations';
import {
  MonitorOverviewItem,
  MonitorOverviewViewItem
} from '../models/monitor';

const INSTITUTIONAL = 'Institutional';
const STOCK = 'Stock';

const NUMBER_FORMAT = '1.0-6';
const DATE_FORMAT = 'MM/DD/YYYY';

const decimalPipe: DecimalPipe = new DecimalPipe('en');
const toNoRoundPercents: NoRoundPercentsPipe = new NoRoundPercentsPipe();

export const CATEGORY_POSITION = 10;
export const INSTITUTIONAL_POSITION = 101;
export const STOCK_POSITION = 102;

export function performFinalTransformations(
  item: MonitorOverviewItem,
  notCategories: string[]
): MonitorOverviewViewItem {
  const subscription: number = item.total / item.amount;
  const maturity = moment.utc(item.maturity).format(DATE_FORMAT);
  const series = item.series;
  const coupon =
    !!item.coupon || item.coupon === 0 ? `${item.coupon.toFixed(3)}%` : '';
  const amount = decimalPipe.transform(item.amount / 1000, NUMBER_FORMAT);
  const total = decimalPipe.transform(item.total / 1000, NUMBER_FORMAT);
  const balance = decimalPipe.transform(item.balance / 1000, NUMBER_FORMAT);
  const formattedSubscription =
    subscription < 1
      ? toNoRoundPercents.transform(subscription * 100)
      : subscription.toFixed(1) + 'x';

  const viewItem: any = {
    ...item,
    subscription,
    formatted: {
      maturity,
      series,
      coupon,
      amount,
      total,
      balance,
      subscription: formattedSubscription
    }
  };
  Object.keys(item)
    .filter((key) => notCategories.indexOf(key) === -1)
    .forEach((category: string) => {
      viewItem.formatted[category] = decimalPipe.transform(
        (item[category] as number) / 1000,
        NUMBER_FORMAT
      );
    });

  return viewItem;
}

export function transformTotals(
  totals: MonitorOverviewItem,
  notCategories: string[]
): MonitorOverviewViewItem {
  const viewTotals = performFinalTransformations(totals, notCategories);
  viewTotals.formatted.maturity = 'Total: (Amounts in $M)';
  return viewTotals;
}

export function getColumnPosition(item: BarChartLegend, index: number): number {
  let result;
  if (item.name === INSTITUTIONAL) {
    result = INSTITUTIONAL_POSITION;
  } else if (item.name === STOCK) {
    result = STOCK_POSITION;
  } else {
    result = CATEGORY_POSITION + index;
  }
  return result;
}

export function generateBaseMonitorOverviewItem(
  categories: BarChartLegend[],
  maturity: GameDayModel.Maturity,
  series: GameDayModel.Series,
  trancheGroups: TrancheGroup[],
  amount = 0
): MonitorOverviewItem {
  const result: MonitorOverviewItem = {
    id: maturity.id,
    maturity: maturity.maturityDate ? maturity.maturityDate.toDate() : null,
    series: series.code,
    coupon: maturity.coupon,
    amount: maturity.size,
    total: amount,
    balance: 0,
    subscription: 0,
    trancheId: getTrancheIdByMaturity(maturity, trancheGroups)
  };

  categories.forEach((category) => {
    result[category.name] = 0;
  });

  return result;
}

export function generateMonitorOverviewItemByMaturity(
  categories: BarChartLegend[],
  maturity: GameDayModel.Maturity,
  trancheGroups: TrancheGroup[]
): MonitorOverviewItem {
  return generateBaseMonitorOverviewItem(
    categories,
    maturity,
    maturity.series,
    trancheGroups
  );
}

export function generateMonitorOverviewItemByOrder(
  categories: BarChartLegend[],
  order: GameDayModel.Order,
  trancheGroups: TrancheGroup[]
): MonitorOverviewItem {
  return generateBaseMonitorOverviewItem(
    categories,
    order.maturity,
    order.series,
    trancheGroups,
    order.amount
  );
}

function isTrancheWithMaturity(
  tranche: Tranche,
  maturity: GameDayModel.Maturity
): boolean {
  return (
    tranche.maturities && tranche.maturities.some((m) => maturity.id === m.id)
  );
}

export function getTrancheIdByMaturity(
  maturity: GameDayModel.Maturity,
  trancheGroups: TrancheGroup[]
): string {
  let selectedTranche: Tranche;

  trancheGroups.some((group: TrancheGroup) => {
    selectedTranche =
      group.tranches &&
      group.tranches.find((tranche) =>
        isTrancheWithMaturity(tranche, maturity)
      );

    if (!selectedTranche) {
      selectedTranche = isTrancheWithMaturity(group, maturity) && group;
    }

    return !!selectedTranche;
  });

  return selectedTranche ? selectedTranche.id : undefined;
}

export function sortOrderMonitorItemByMaturity(
  a: MonitorOverviewViewItem,
  b: MonitorOverviewViewItem
): number {
  if (a.maturity < b.maturity) {
    return -1;
  }
  if (a.maturity > b.maturity) {
    return 1;
  }
  if (a.series < b.series) {
    return -1;
  }
  if (a.series > b.series) {
    return 1;
  }
  if (a.coupon < b.coupon) {
    return -1;
  }
  if (a.coupon > b.coupon) {
    return 1;
  }
  if (a.amount > b.amount) {
    return -1;
  }
  if (a.amount < b.amount) {
    return 1;
  }
  return 0;
}
